import { tWithKeyAttr as t } from "../../i18nHelper";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./filterButton.module.css";
import filterIcon from "../../images/filter_icon.svg";
import { useAppSelector } from "../../services/hooks";
import { statusesSelector } from "../../services/selectors/user.selectors";
import {
  filterOptionsMain,
  filterOptionsMainWithStatus,
} from "../../utils/constants";
import {
  TTherapistWithUsers,
  TUsersWithOutTherapist,
} from "../../services/types/user.types";
import {
  isToday,
  isTTherapistWithUsersArray,
  extractUniqueQuestionsAndAnswers,
  matchesSpecificQuestionFilters,
} from "../../utils/utils";
import SpecificQuestionFilterModal from "./SpecificQuestionFilterModal";

function FilterButtonMainPage<
  T extends TTherapistWithUsers | TUsersWithOutTherapist
>({
  documentsToFilter,
  setDocumentsToFilter,
  fakeCompanyId
}: {
  documentsToFilter: T[] | undefined;
  setDocumentsToFilter: (users: TUsersWithOutTherapist[] | TTherapistWithUsers[] | undefined, fakeCompanyId?: string) => void;
  fakeCompanyId?: string;
}) {
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left?: number; right?: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  
  const statuses = useAppSelector(statusesSelector);
  const [options, setOptions] = useState(filterOptionsMain);
  const [filterValue, setFilterValue] = useState<undefined | string>(undefined);
  const [statusFilter, setStatusFilter] = useState<undefined | number>(
    undefined
  );
  const [specificFilters, setSpecificFilters] = useState<{ question: string; answers: string[] }[]>([]);
  const [showSpecificModal, setShowSpecificModal] = useState(false);
  const today = new Date();

  const questionsAndAnswers = useMemo(
    () => extractUniqueQuestionsAndAnswers(documentsToFilter ?? []),
    [documentsToFilter]
  );

  useEffect(() => {
    if (isTTherapistWithUsersArray(documentsToFilter) && !fakeCompanyId) {
      setOptions(filterOptionsMainWithStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Professional click-outside and scroll handler
  useEffect(() => {
    if (!filtersVisible) return;

    // If dropdown overflows viewport bottom, reposition upward
    const raf = requestAnimationFrame(() => {
      if (!buttonRef.current) return;
      const dropdown = containerRef.current?.querySelector(`.${styles.filterOptionsFixed}`) as HTMLElement | null;
      if (dropdown) {
        const dropdownHeight = dropdown.getBoundingClientRect().height;
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        if (dropdownHeight > spaceBelow) {
          // Not enough space below — open upward
          setDropdownPosition(prev => prev ? { ...prev, top: buttonRect.top - dropdownHeight - 5 } : prev);
        }
      }
    });

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setFiltersVisible(false);
      }
    };

    const handleScroll = (event: Event) => {
      if (!event.target || containerRef.current?.contains(event.target as Node)) return;
      setFiltersVisible(false);
    };

    // Use timeout to avoid closing immediately on open
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }, 0);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [filtersVisible]);

  useEffect(() => {
   
    if (filterValue === "clearFilters") {
      setStatusFilter(0);
      setSpecificFilters([]);
      setFilterValue(undefined);
    } else if (filterValue === "today") {
      setStatusFilter(0);
      if (documentsToFilter) {
        if (fakeCompanyId) {
          setDocumentsToFilter(
            documentsToFilter.filter((d) => isToday(d.timestamp)), fakeCompanyId
          );
        }
        else {
          setDocumentsToFilter(
            documentsToFilter.filter((d) => isToday(d.timestamp))
          );
        }
      }
    } else if (filterValue === "week") {
      setStatusFilter(0);
      const startOfCurrentWeek = new Date(today);
      startOfCurrentWeek.setDate(today.getDate() - today.getDay());

      // Calculate the start of the last week (Sunday)
      const startOfLastWeek = new Date(startOfCurrentWeek);
      startOfLastWeek.setDate(startOfCurrentWeek.getDate() - 7);

      documentsToFilter &&
        fakeCompanyId ?
        setDocumentsToFilter(
          documentsToFilter.filter(
            (d) =>
              new Date(d.timestamp) >= startOfLastWeek &&
              new Date(d.timestamp) <= today
          ), fakeCompanyId
        ) :
        documentsToFilter &&
        setDocumentsToFilter(
          documentsToFilter.filter(
            (d) =>
              new Date(d.timestamp) >= startOfLastWeek &&
              new Date(d.timestamp) <= today
          )
        );
    } else if (filterValue === "month") {
      setStatusFilter(0);
      const startOfCurrentMonth = new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      );
      fakeCompanyId ? setDocumentsToFilter(
        documentsToFilter?.filter(
          (d) => new Date(d.timestamp) >= startOfCurrentMonth
        ), fakeCompanyId
      ) :
        setDocumentsToFilter(
          documentsToFilter?.filter(
            (d) => new Date(d.timestamp) >= startOfCurrentMonth
          )
        );
    }
    else if (filterValue === "rrm") {
      setStatusFilter(0);
      const result = documentsToFilter?.filter(item => item.generalRRMSEQ !== null)
        .sort((a, b) => b.generalRRMSEQ! - a.generalRRMSEQ!);
      fakeCompanyId ? setDocumentsToFilter(
        result, fakeCompanyId
      ) :
        setDocumentsToFilter(
          result
        );
    }
    else if (filterValue === "cycle") {
      setStatusFilter(0);
      const result = documentsToFilter?.filter(item => item.cycle !== null)
        .sort((a, b) => a.cycle! - b.cycle!);
      fakeCompanyId ? setDocumentsToFilter(
        result, fakeCompanyId
      ) :
        setDocumentsToFilter(
          result
        );
    }
    else if (filterValue === "specificQuestion") {
      const hasActive = specificFilters.some((f) => f.answers.length > 0);
      if (hasActive && documentsToFilter) {
        const result = documentsToFilter.filter((d) =>
          matchesSpecificQuestionFilters(d.specificFields, specificFilters)
        );
        fakeCompanyId
          ? setDocumentsToFilter(result, fakeCompanyId)
          : setDocumentsToFilter(result);
      } else {
        fakeCompanyId
          ? setDocumentsToFilter(documentsToFilter, fakeCompanyId)
          : setDocumentsToFilter(documentsToFilter);
      }
    }
    else {
      fakeCompanyId ? setDocumentsToFilter(documentsToFilter, fakeCompanyId) : setDocumentsToFilter(documentsToFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, documentsToFilter, specificFilters]);

  const handleToggleDropdown = useCallback(() => {
    if (!filtersVisible && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const isRTL = document.dir === 'rtl' || document.documentElement.dir === 'rtl';
      
      setDropdownPosition(
        isRTL
          ? { top: rect.bottom + 5, right: window.innerWidth - rect.right }
          : { top: rect.bottom + 5, left: rect.left }
      );
    }
    setFiltersVisible(prev => !prev);
  }, [filtersVisible]);

  const onStatusChange = useCallback((status: number) => {
    setStatusFilter(status);
    if (isTTherapistWithUsersArray(documentsToFilter)) {
      const newValue = documentsToFilter.filter((d) => d.status === status);
      fakeCompanyId ? setDocumentsToFilter(newValue, fakeCompanyId) : setDocumentsToFilter(newValue);
    }
  }, [documentsToFilter, fakeCompanyId, setDocumentsToFilter]);

  return (
    <div className={`${styles.container} ${styles.center}`} ref={containerRef}>
      <button
        ref={buttonRef}
        className={`${styles.btn} ${styles.btn_type_filter} hover`}
        onClick={handleToggleDropdown}
      >
        <img className={styles.filterIcon} src={filterIcon} alt={t("FILTER_MAIN_PAGE_BUTTON_ALT")} />      
        {t("FILTER_MAIN_PAGE_BUTTON_TEXT")}
      </button>
      {filtersVisible && dropdownPosition && (
        <ul
          className={styles.filterOptionsFixed}
          style={{
            top: `${dropdownPosition.top}px`,
            left: dropdownPosition.left !== undefined ? `${dropdownPosition.left}px` : 'auto',
            right: dropdownPosition.right !== undefined ? `${dropdownPosition.right}px` : undefined
          }}
        >
          {options.filter((f) => f.value !== "specificQuestion" || questionsAndAnswers.size > 0).map((f, index) => (
            <li
              key={index}
              className={`${styles.filterOption} ${
                f.value === "status" && styles.filterOption_type_status
              } ${
                filterValue === "status" && f.value === "status" && styles.filterOption_type_status_open
              }`}
              onClick={() => {
                if (f.value === "specificQuestion") {
                  setFiltersVisible(false);
                  setShowSpecificModal(true);
                  return;
                }
                setFilterValue(f.value);
                if (f.value !== "status") {
                  setFiltersVisible(false);
                }
              }}
            >
              <input
                type="radio"
                value={f.value}
                id={f.value}
                name="sort"
                className={styles.radio}
                checked={filterValue === f.value}
                onChange={() => { }}
              />
              <label htmlFor={f.value} className={styles.label}>
                {t(f.label)}
              </label>
              {f.value === "status" && (
                <ul className={styles.statusOptions}>
                  {statuses.map((s) => (
                    <li
                      key={s.id}
                      className={`${styles.filterOption} ${styles.statusOption}`}
                      onClick={() => {
                        onStatusChange(s.id);
                        setFiltersVisible(false);
                      }}
                    >
                      <input
                        type="radio"
                        value={s.id}
                        id={s.id.toString()}
                        name="statusSort"
                        className={`${styles.radio} ${styles.radio_type_status}`}
                        checked={statusFilter === s.id}
                        onChange={() => { }}
                      />
                      <label htmlFor={s.id.toString()} className={styles.label}>
                        {s.desc}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          <li
            className={styles.filterOption}
            onClick={() => {
              setFilterValue("clearFilters");
              setFiltersVisible(false);
            }}
          >
            <input
              type="radio"
              value="clearFilters"
              id="clearFilters"
              name="sort"
              className={styles.radio}
              checked={filterValue === "clearFilters"}
              onChange={() => { }}
            />
         <label htmlFor="clearFilters" className={styles.label}>
  {t("FILTER_MAIN_PAGE_CLEAR_LABEL")}
</label>
          </li>
        </ul>
      )}
      {!!filterValue && <span>!</span>}
      {showSpecificModal && (
        <SpecificQuestionFilterModal
          questionsAndAnswers={questionsAndAnswers}
          initialFilters={specificFilters}
          onApply={(filters) => {
            setSpecificFilters(filters);
            setFilterValue(filters.some((f) => f.answers.length > 0) ? "specificQuestion" : undefined);
          }}
          onClose={() => setShowSpecificModal(false)}
        />
      )}
    </div>
  );
}

export default FilterButtonMainPage;
